import * as React from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Box, Button, Table, Text } from '@factorialco/deprecated-design-system'
import { Features } from 'generated/featureDefinitions'
import { TimeTrackingWorkedHoursWorkedBreaksFragment } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import UserAvatar from 'modules/core/components/UserAvatar'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import { employeeUser } from 'modules/core/store/collections/users'

import InconsistenciesAlertCountsTag from 'modules/attendance/components/InconsistenciesAlertCountsTag'
import MinutesDistribution from 'modules/attendance/components/MinutesDistribution'
import MinutesToText from 'modules/attendance/components/MinutesToText'
import { BalanceBreakdownTooltip } from 'modules/attendance/components/TimeTracking/BalanceBreakdownTooltip'
import { EstimatedHours } from 'modules/attendance/components/TimeTracking/EstimatedHours'
import type CustomPeriodForm from 'modules/attendance/components/TimeTracking/Layout/form'
import { WorkedHours } from 'modules/attendance/components/TimeTracking/WorkedHours'
import { aggregateShiftLocationType } from 'modules/attendance/hooks/shift_locations/useGetAggreagteShiftLocations'
import { BalanceBreakdown } from 'modules/attendance/hooks/useFetchAttendanceBalanceBreakdown'
import { Inconsistencies } from 'modules/attendance/hooks/useShiftInconsistencies'
import { useAttendanceDateNavigator } from 'modules/attendance/lib/attendanceDateNavigatorContext'
import { useAttendanceFeatures } from 'modules/attendance/lib/attendanceFeaturesContext'
import { periodEmployee } from 'modules/attendance/lib/periodEmployee'
import { PopoverTypeToShow } from 'modules/attendance/lib/types'
import type { ClockInStatus } from 'modules/attendance/pages/CustomPeriodsPage'
import type AttendanceSetting from 'modules/attendance/store/models/AttendanceSetting'
import type OvertimeRequest from 'modules/attendance/store/models/OvertimeRequest'
import type Period from 'modules/attendance/store/models/Period'
import employees from 'modules/employees/store/collections/employees'

import { getColumnsColspan } from '../../tableColSpans'
import AttendanceItem from './AttendanceItem'
import IncidentShiftLocation from './_IncidentShiftLocation'
import SelectEmployee from './_SelectEmployee'
import WithinRadiusShiftLocation from './_WithinRadiusShiftLocation'

interface Props {
  customPeriod: Period
  workedBreaks: TimeTrackingWorkedHoursWorkedBreaksFragment[]
  selected: Period[]
  onToggleRange: (item: Period) => void
  onToggleSelectedItem: (item: Period) => void
  customPeriodForm: CustomPeriodForm
  attendanceSettings: AttendanceSetting[]
  overtimeRequests: OvertimeRequest[]
  balanceBreakdown?: BalanceBreakdown
  hasGeofencingFeatureFlag: boolean
  aggregateIncidentShiftLocation?: aggregateShiftLocationType
  showHourDistributionColumn: boolean
  canSendEmailReminder: boolean
  inconsistencies?: Inconsistencies
  clockInStatus: ClockInStatus | undefined
}

const Item = ({
  customPeriod,
  workedBreaks,
  selected,
  onToggleRange,
  onToggleSelectedItem,
  customPeriodForm,
  attendanceSettings,
  overtimeRequests,
  balanceBreakdown,
  hasGeofencingFeatureFlag,
  aggregateIncidentShiftLocation,
  showHourDistributionColumn,
  canSendEmailReminder,
  inconsistencies,
  clockInStatus,
}: Props) => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const { pathParams } = useAttendanceDateNavigator()
  const { featureAvailability: hasRealTimeAttendance } = useAttendanceFeatures(
    Features.time_tracking_real_time_attendance
  )
  const { featureAvailability: hasTimeTrackingInconsistencies } = useAttendanceFeatures(
    Features.time_tracking_time_alerts
  )

  const user = employeeUser(periodEmployee(customPeriod, employees))

  const rowDisabled = canSendEmailReminder
    ? clockInStatus?.status === 'clocked_in' || clockInStatus?.status === 'in_a_break'
    : false

  const rowSelected = selected.includes(customPeriod) && !rowDisabled
  const showSelectEmployee = rowSelected || customPeriod.notInProgress || canSendEmailReminder

  const { isApprovable: canApprovePeriod, isDaily } = customPeriod
  const { employee_id: employeeId, balance_minutes, worked_half_days } = customPeriod.attributes

  const COLSPANS = getColumnsColspan()
  const hasGeofencing = attendanceSettings.some(
    (attendanceSetting) => attendanceSetting.hasGeofencingEnabled
  )
  const pathnames = location.pathname.split('/')

  const hasIncidentShiftLocation =
    aggregateIncidentShiftLocation && aggregateIncidentShiftLocation.shiftLocationsCount > 0

  const hasOvertimeAuthorization = attendanceSettings.some(
    (attendanceSetting) => attendanceSetting.hasOvertimeAuthorization
  )
  const requestedOvertimeHours = overtimeRequests.reduce((acc, req) => acc + req.hours, 0)
  const canUpdateUser = canApprovePeriod || canSendEmailReminder

  const onClickTableArrow = (event: React.SyntheticEvent<Element, Event>) => {
    event.stopPropagation()
    track(trackingEvents.timeTracking.goToEmployeeTimeSheet, {
      geofencing_incidences: aggregateIncidentShiftLocation?.shiftLocationsCount ?? 0,
      type_view: pathnames.includes('time-tracking') ? 'admin' : 'employee',
    })

    const params = {
      ...pathParams,
      employeeId,
    }
    const path = navigation.timeTracking.employee.periodTypeYearMonthDayEmployeeId.fullPath(params)
    return navigate(path)
  }

  const estimatedTime = {
    minutes: customPeriod.attributes.estimated_minutes,
    regularMinutes: customPeriod.attributes.estimated_regular_minutes,
    overtimeMinutes: customPeriod.attributes.estimated_overtime_minutes,
    contractMinutes: customPeriod.attributes.estimated_contract_minutes,
    absencesMinutes: customPeriod.attributes.estimated_absences_minutes,
  }

  const estimated = isDaily ? '-' : <EstimatedHours estimatedTime={estimatedTime} />
  const worked = isDaily ? (
    i18n.t('time_tracking.day_abbreviation', {
      numberOfDays: worked_half_days / 2,
    })
  ) : (
    <WorkedHours
      period={customPeriod}
      workedBreaks={workedBreaks}
      hasOvertimeAuthorization={hasOvertimeAuthorization}
      requestedOvertimeHours={requestedOvertimeHours}
    />
  )

  const BalanceOnly = () => (
    <>
      {isDaily ? (
        '-'
      ) : !!balanceBreakdown ? (
        <BalanceBreakdownTooltip breakdown={balanceBreakdown} />
      ) : (
        <MinutesToText value={balance_minutes} withPlusSign />
      )}
    </>
  )

  const BalanceWithLocation = () => {
    return (
      <Box gap="s4">
        <BalanceOnly />
        {hasGeofencing && (
          <Box flexDirection="row">
            {hasIncidentShiftLocation ? (
              <IncidentShiftLocation count={aggregateIncidentShiftLocation.shiftLocationsCount} />
            ) : (
              <WithinRadiusShiftLocation />
            )}
          </Box>
        )}
      </Box>
    )
  }

  const onClickRow = (event: React.MouseEvent<HTMLTableRowElement, MouseEvent>) => {
    event.stopPropagation()

    if (
      !showSelectEmployee ||
      !canUpdateUser ||
      !onToggleRange ||
      !onToggleSelectedItem ||
      rowDisabled
    )
      return

    if (event.shiftKey) onToggleRange(customPeriod)
    else onToggleSelectedItem(customPeriod)
  }

  useEffect(() => {
    if (rowSelected) {
      track(trackingEvents.timeTracking.employeeSelectRow, {
        employee_id: employeeId,
      })
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowSelected])

  const showUserRealTimeData =
    !!clockInStatus && canSendEmailReminder && !!clockInStatus.status && hasRealTimeAttendance

  const { periodType = '' } = useParams()

  return (
    <Table.Row onClick={onClickRow}>
      {showSelectEmployee && (
        <SelectEmployee
          rowSelected={rowSelected}
          canUpdateUser={canUpdateUser}
          rowDisabled={rowDisabled}
        />
      )}
      <Table.Cell colSpan={COLSPANS.EMPLOYEE_COLUMN} wide>
        {showUserRealTimeData ? (
          <AttendanceItem clockInStatus={clockInStatus} />
        ) : (
          <Box overflowX="hidden" flexDirection="row" alignItems="center" gap="s8">
            <UserAvatar size="s32" user={user} />
            <Text.Normal weight="medium" ellipsis color="grey1000">
              {user.fullName()}
            </Text.Normal>
          </Box>
        )}
      </Table.Cell>
      <Table.Cell colSpan={COLSPANS.ESTIMATED_HOURS_COLUMN} wide>
        {estimated}
      </Table.Cell>
      <Table.Cell colSpan={COLSPANS.WORKED_HOURS_COLUMN} wide>
        {worked}
      </Table.Cell>
      {!hasGeofencingFeatureFlag && (
        <Table.Cell colSpan={COLSPANS.HOUR_BALANCE_COLUMN} wide>
          <BalanceOnly />
        </Table.Cell>
      )}
      {showHourDistributionColumn && (
        <Table.Cell colSpan={COLSPANS.DISTRIBUTION_COLUMN} wide>
          <MinutesDistribution
            startOn={customPeriodForm.startOn}
            period={customPeriod}
            wide={customPeriodForm.numberOfDays <= 14}
          />
        </Table.Cell>
      )}
      {hasGeofencingFeatureFlag && (
        <Table.Cell colSpan={COLSPANS.HOUR_BALANCE_COLUMN} wide>
          <BalanceWithLocation />
        </Table.Cell>
      )}
      {hasTimeTrackingInconsistencies && (
        <Table.Cell colSpan={COLSPANS.ALERTS_COLUMN} wide>
          <InconsistenciesAlertCountsTag
            popoverTypeToShow={PopoverTypeToShow.Count}
            inconsistencies={inconsistencies}
            page="time_tracking"
            timeRange={periodType}
          />
        </Table.Cell>
      )}
      <Table.Cell wide colSpan={COLSPANS.NAVIGATION_ICON_COLUMN}>
        <Button onClick={onClickTableArrow} type="secondary" iconName="arrow-right" />
      </Table.Cell>
    </Table.Row>
  )
}

export default observer(Item)
